/** Data access for identity administration: members, invitations, roles, permissions, teams, audit. */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";

import { api, type Schemas } from "@/api/client";
import { unwrap } from "@/api/problem";
import { compactParams, type Paged, usePagedQuery } from "@/shared/lib/paging";

export type Member = Schemas["MemberOut"];
export type MemberPatch = Schemas["MemberPatch"];
export type Invitation = Schemas["InvitationOut"];
export type InviteIn = Schemas["InviteIn"];
export type Role = Schemas["RoleOut"];
export type RoleIn = Schemas["RoleIn"];
export type RolePatch = Schemas["RolePatch"];
export type Permission = Schemas["PermissionOut"];
export type Team = Schemas["TeamOut"];
export type TeamIn = Schemas["TeamIn"];
export type AuditEvent = Schemas["AuditEventOut"];
export type SsoConnection = Schemas["SsoConnectionOut"];
export type SsoConnectionIn = Schemas["SsoConnectionIn"];
export type SsoConnectionPatch = Schemas["SsoConnectionPatch"];

/** Membership kinds. */
export const MEMBER_KINDS = ["staff", "vendor", "customer", "readonly"] as const;
/** Membership statuses. */
export const MEMBER_STATUSES = ["invited", "active", "disabled"] as const;

function invalidate(qc: ReturnType<typeof useQueryClient>, ...keys: string[]): void {
  for (const key of keys) {
    void qc.invalidateQueries({ queryKey: [key] });
  }
}

/** Members (paged). */
export function useMembers(filters: { q?: string; status?: string }): Paged<Member> {
  return usePagedQuery(["members", filters], async (cursor) =>
    unwrap(
      await api.GET("/api/v1/identity/members", {
        params: { query: compactParams({ ...filters, cursor, limit: 50 }) },
      }),
    ),
  );
}

/** Edit a member. */
export function useUpdateMember(): UseMutationResult<
  Member,
  Error,
  { id: string; body: MemberPatch }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, body }) =>
      unwrap(
        await api.PATCH("/api/v1/identity/members/{member_id}", {
          params: { path: { member_id: id } },
          body,
        }),
      ),
    onSuccess: () => invalidate(qc, "members", "picker"),
  });
}

/** Change only future booked cost defaults without general membership-management access. */
export function useSetMemberCostRate(): UseMutationResult<
  Member,
  Error,
  { id: string; hourly_rate: number | null }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, hourly_rate }) =>
      unwrap(
        await api.PATCH("/api/v1/identity/members/{member_id}/cost-rate", {
          params: { path: { member_id: id } },
          body: { hourly_rate },
        }),
      ),
    onSuccess: () => invalidate(qc, "members"),
  });
}

/** Pending invitations. */
export function useInvitations(): UseQueryResult<Invitation[]> {
  return useQuery({
    queryKey: ["invitations"],
    queryFn: async () => unwrap(await api.GET("/api/v1/identity/invitations")),
  });
}

/** Send an invitation. */
export function useInvite(): UseMutationResult<Invitation, Error, InviteIn> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body) => unwrap(await api.POST("/api/v1/identity/invitations", { body })),
    onSuccess: () => invalidate(qc, "invitations"),
  });
}

/** Revoke an invitation. */
export function useRevokeInvitation(): UseMutationResult<unknown, Error, string> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id) =>
      unwrap(
        await api.DELETE("/api/v1/identity/invitations/{invitation_id}", {
          params: { path: { invitation_id: id } },
        }),
      ),
    onSuccess: () => invalidate(qc, "invitations"),
  });
}

/** Roles. */
export function useRoles(): UseQueryResult<Role[]> {
  return useQuery({
    queryKey: ["roles"],
    queryFn: async () => unwrap(await api.GET("/api/v1/identity/roles")),
    staleTime: 60_000,
  });
}

/** Permission catalogue. */
export function usePermissions(): UseQueryResult<Permission[]> {
  return useQuery({
    queryKey: ["permissions"],
    queryFn: async () => unwrap(await api.GET("/api/v1/identity/permissions")),
    staleTime: 10 * 60_000,
  });
}

/** Create a role. */
export function useCreateRole(): UseMutationResult<Role, Error, RoleIn> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body) => unwrap(await api.POST("/api/v1/identity/roles", { body })),
    onSuccess: () => invalidate(qc, "roles"),
  });
}

/** Edit a role. */
export function useUpdateRole(): UseMutationResult<Role, Error, { id: string; body: RolePatch }> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, body }) =>
      unwrap(
        await api.PATCH("/api/v1/identity/roles/{role_id}", {
          params: { path: { role_id: id } },
          body,
        }),
      ),
    onSuccess: () => invalidate(qc, "roles", "members"),
  });
}

/** Delete a custom role. */
export function useDeleteRole(): UseMutationResult<unknown, Error, string> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id) =>
      unwrap(
        await api.DELETE("/api/v1/identity/roles/{role_id}", {
          params: { path: { role_id: id } },
        }),
      ),
    onSuccess: () => invalidate(qc, "roles"),
  });
}

/** Teams. */
export function useTeams(): UseQueryResult<Team[]> {
  return useQuery({
    queryKey: ["teams"],
    queryFn: async () => unwrap(await api.GET("/api/v1/identity/teams")),
  });
}

/** Create or replace a team. */
export function useSaveTeam(): UseMutationResult<Team, Error, { id?: string; body: TeamIn }> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, body }) =>
      id
        ? unwrap(
            await api.PUT("/api/v1/identity/teams/{team_id}", {
              params: { path: { team_id: id } },
              body,
            }),
          )
        : unwrap(await api.POST("/api/v1/identity/teams", { body })),
    onSuccess: () => invalidate(qc, "teams"),
  });
}

/** Delete a team. */
export function useDeleteTeam(): UseMutationResult<unknown, Error, string> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id) =>
      unwrap(
        await api.DELETE("/api/v1/identity/teams/{team_id}", {
          params: { path: { team_id: id } },
        }),
      ),
    onSuccess: () => invalidate(qc, "teams"),
  });
}

/** Audit events (paged). */
export function useAuditEvents(filters: {
  category?: string;
  entity_type?: string;
}): Paged<AuditEvent> {
  return usePagedQuery(["audit", filters], async (cursor) =>
    unwrap(
      await api.GET("/api/v1/identity/audit-events", {
        params: { query: compactParams({ ...filters, cursor, limit: 50 }) },
      }),
    ),
  );
}

/** Group permissions by module, preserving catalogue order. */
export function groupPermissions(perms: Permission[]): Map<string, Permission[]> {
  const out = new Map<string, Permission[]>();
  for (const p of perms) {
    const list = out.get(p.module) ?? [];
    list.push(p);
    out.set(p.module, list);
  }
  return out;
}

/** Tenant SSO connections. */
export function useSsoConnections(): UseQueryResult<SsoConnection[]> {
  return useQuery({
    queryKey: ["sso-connections"],
    queryFn: async () => unwrap(await api.GET("/api/v1/identity/sso-connections")),
  });
}

/** Create an SSO connection. */
export function useCreateSsoConnection(): UseMutationResult<SsoConnection, Error, SsoConnectionIn> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body) =>
      unwrap(await api.POST("/api/v1/identity/sso-connections", { body })),
    onSuccess: () => invalidate(qc, "sso-connections"),
  });
}

/** Update an SSO connection. */
export function useUpdateSsoConnection(): UseMutationResult<
  SsoConnection,
  Error,
  { id: string; body: SsoConnectionPatch }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, body }) =>
      unwrap(
        await api.PATCH("/api/v1/identity/sso-connections/{connection_id}", {
          params: { path: { connection_id: id } },
          body,
        }),
      ),
    onSuccess: () => invalidate(qc, "sso-connections"),
  });
}

/** Delete an SSO connection. */
export function useDeleteSsoConnection(): UseMutationResult<unknown, Error, string> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id) =>
      unwrap(
        await api.DELETE("/api/v1/identity/sso-connections/{connection_id}", {
          params: { path: { connection_id: id } },
        }),
      ),
    onSuccess: () => invalidate(qc, "sso-connections"),
  });
}
